import { DigitalWalletRowResult } from "@/models/digital.wallet";
import { Box, Grid, List, ListItem, Toolbar } from "@mui/material";
import { FC, useEffect, useRef, useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LinkElementStyled from "../styled/link.element.styled";
import StatusActive from "../../assets/icons/status-active.svg";
import StatusInactive from "../../assets/icons/status-inactive.svg";
import DetailButtonStyled from "../styled/detail.button.styled";
import { formatDate, formatAmount } from "@/util";
import { useNavigate } from "react-router-dom";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import filtersStore from "@/stores/filtersStore";

interface IDigitalWalletListGrid {
    sortBy: Function;
    wallets: DigitalWalletRowResult[];
    isSearch: boolean;
    fetchMore: Function;
    hasNext: boolean;
    orderBy: string;
    asc: boolean;
    collapseFilterCounter: number;
}

const DigitalWalletListGrid: FC<IDigitalWalletListGrid> = (props: IDigitalWalletListGrid) => {
    const { t } = useTranslation();
    const gridEl = useRef() as React.MutableRefObject<HTMLInputElement>;
    const navigate = useNavigate();

    const openTransactions = (row: DigitalWalletRowResult) => {
        employeeBreadcrumbStore.update(row.employeeName);
        navigate({
            pathname: "/digital-wallet/list/" + row.id + "/transactions",
            search: `?returnUrl=${window.location.pathname}`,
        });
    };

    const openEmployeePage = (id: number | undefined, employeeName: string) => {
        if (!id) {
            return;
        }

        filtersStore.setEmployeeWalletFilterData(null);
        filtersStore.setEmployeeOrderFilterData(null);
        filtersStore.setEmployeeDocFilterData(null);
        employeeBreadcrumbStore.update(employeeName);
        navigate({
            pathname: `/employees/${id}/info`,
            search: `?returnUrl=${window.location.pathname}`,
        });
    };

    /* infinite scroll */
    const scrollListener = useCallback(() => {
        let bottom = gridEl.current.scrollHeight - gridEl.current.clientHeight;
        if (gridEl.current.scrollTop > 0 && gridEl.current.scrollTop >= bottom && props.hasNext) {
            filtersStore.setDigitalWalletsFilterData(null);
            props.fetchMore();
        }
    }, [gridEl, props]);
    useLayoutEffect(() => {
        const gridRef = gridEl.current;
        gridRef.addEventListener("scroll", scrollListener);
        return () => {
            gridRef.removeEventListener("scroll", scrollListener);
        };
    }, [scrollListener]);
    /* infinite scroll */

    function getArrowClass(sort: string) {
        if (sort === props.orderBy) {
            if (props.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const updateGridHeight = () => {
        let offsetHeight = 125;
        const walletContainer = document.getElementById("digital-wallet-search-grid");
        if (!walletContainer) {
            return;
        }
        const element = document.getElementById("digital-wallet-top-block");
        if (element) {
            offsetHeight += Number(element.clientHeight);
            console.log(element.clientHeight);
        }

        walletContainer.style.height = `calc(100vh - ${offsetHeight}px)`;
    };

    useEffect(() => {
        updateGridHeight();
    });

    return (
        <Grid
            id="digital-wallet-search-grid"
            className="digital-wallet-search-grid"
            ref={gridEl}
            sx={{
                width: "100%",
                position: "relative",
            }}
        >
            {props.wallets.length > 0 && (
                <List className="digital-wallet-search-list">
                    <ListItem className="digital-wallet-search-list-header">
                        <Grid className="digital-wallet-search-grid-header">
                            <Grid item width="10%" onClick={() => props.sortBy("WalletStatus")} className="sortable">
                                <span>
                                    {t("general.SearchFilterWalletStatus")}
                                    <i className={`icon-f ${getArrowClass("WalletStatus")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid
                                item
                                width="10%"
                                onClick={() => props.sortBy("ExpiredDate")}
                                className="sortable text-align-right"
                            >
                                <span>
                                    {t("digitalWallet.ExpiryDate")}
                                    <i className={`icon-f ${getArrowClass("ExpiredDate")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid item width="10%" onClick={() => props.sortBy("EmployeesName")} className="sortable">
                                <span>
                                    {t("general.SearchFilterEmployeesName")}
                                    <i className={`icon-f ${getArrowClass("EmployeesName")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid
                                item
                                width="10%"
                                onClick={() => props.sortBy("EmployeeNumber")}
                                className="sortable text-align-right"
                            >
                                <span>
                                    {t("employee.EmployeeNumber")}
                                    <i className={`icon-f ${getArrowClass("EmployeeNumber")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid item width="10%">
                                {t("general.SearchFilterEmployeesStatus")}
                            </Grid>
                            <Grid item width="10%" className="text-align-right">
                                {t("employee.Phone")}
                            </Grid>
                            <Grid
                                item
                                width="10%"
                                onClick={() => props.sortBy("CurrentBalance")}
                                className="sortable text-align-right"
                            >
                                <span>
                                    {t("digitalWallet.CurrentBalance")}
                                    <i className={`icon-f ${getArrowClass("CurrentBalance")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid
                                item
                                width="10%"
                                onClick={() => props.sortBy("LastUseDate")}
                                className="sortable text-align-right"
                            >
                                <span>
                                    {t("digitalWallet.LastUseDate")}
                                    <i className={`icon-f ${getArrowClass("LastUseDate")} fs-12`} />
                                </span>
                            </Grid>
                            <Grid item width="10%" className="text-align-right">
                                {t("digitalWallet.LastUsedAmount")}
                            </Grid>
                            <Grid item width="10%"></Grid>
                        </Grid>
                    </ListItem>
                    {props.wallets.map((row) => (
                        <ListItem className="digital-wallet-search-list-item" key={row.id}>
                            <Grid className="digital-wallet-search-grid-body">
                                <Grid item width="10%">
                                    <Box sx={{ display: "flex" }}>
                                        <Box
                                            component="img"
                                            sx={{
                                                height: "8px",
                                                width: "8px",
                                                marginTop: "6px",
                                            }}
                                            alt={t("general.Active")}
                                            src={row.status === "Active" ? StatusActive : StatusInactive}
                                        />
                                        <Box sx={{ padding: "0 5px" }}>
                                            {t(`enums.DigitalWalletStatus.${row.status}`)}
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    {formatDate(row.expiredDate)}
                                </Grid>
                                <Grid item width="10%">
                                    {row.employeeName}
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    <LinkElementStyled
                                        onClick={() => openEmployeePage(row.employeeId, row.employeeName)}
                                    >
                                        {row.employeeNumber}
                                    </LinkElementStyled>
                                </Grid>
                                <Grid item width="10%">
                                    {row.employeeStatus ? (
                                        <Box sx={{ display: "flex" }}>
                                            <Box
                                                component="img"
                                                sx={{
                                                    height: "8px",
                                                    width: "8px",
                                                    marginTop: "6px",
                                                }}
                                                alt={t("general.Active")}
                                                src={StatusActive}
                                            />
                                            <Box sx={{ padding: "0 5px" }}>{t("general.Active")}</Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: "flex" }}>
                                            <Box
                                                component="img"
                                                sx={{
                                                    height: "8px",
                                                    width: "8px",
                                                    marginTop: "6px",
                                                }}
                                                alt={t("general.Inactive")}
                                                src={StatusInactive}
                                            />
                                            <Box sx={{ padding: "0 5px" }}>{t("general.Inactive")}</Box>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    {row.employeePhone}
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    {formatAmount(row.currentBalance, row.currencyId)}
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    {row.lastUseDate && formatDate(row.lastUseDate)}
                                </Grid>
                                <Grid item width="10%" className="text-align-right">
                                    {formatAmount(row.lastUsedAmount, row.currencyId)}
                                </Grid>
                                <Grid item width="10%">
                                    <DetailButtonStyled onClick={() => openTransactions(row)}>
                                        {t("breadcrumbs.DigitalWalletTransactions")}
                                    </DetailButtonStyled>
                                </Grid>
                            </Grid>
                        </ListItem>
                    ))}
                </List>
            )}
        </Grid>
    );
};

export default DigitalWalletListGrid;
